import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FromToLabel } from '../../search-page/search-param.model';
import { SE_UI_DATE_FORMATS } from '../se-ui-date-formats';

@Component({
    selector: 'se-from-to',
    templateUrl: './se-from-to.component.html',
    styleUrls: ['./se-from-to.component.scss'],
    providers: [{ provide: MAT_DATE_FORMATS, useValue: SE_UI_DATE_FORMATS }],
    standalone: false,
})
export class SeFromToComponent implements OnInit, OnDestroy {
    // Costanti
    today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59);
    minFromDate = new Date(1900, 1, 1, 0, 0, 0);
    maxFromDate = new Date(2050, 12, 31, 23, 59, 59);
    minToDate = new Date(1900, 1, 1, 0, 0, 0);
    maxToDate = new Date(2050, 12, 31, 23, 59, 59);

    // Matcher per gli errori
    fromPickerMatcher: FromToPickerStateMatcher = new FromToPickerStateMatcher();
    toPickerMatcher: FromToPickerStateMatcher = new FromToPickerStateMatcher();

    private isOnErrorFromSubscriber!: Subscription;
    private isOnErrorToSubscriber!: Subscription;

    @Input()
    name!: string;

    @Input()
    id!: string;

    @Input()
    disabled: boolean = false;

    @Input()
    autofocus: boolean = false;

    @Input()
    placeholder: FromToLabel = {
        from: 'From',
        to: 'To',
        label: 'test',
    };

    @Input()
    fromToModel: { from: moment.Moment | Date | undefined; to: moment.Moment | Date | undefined; } = {
        from: undefined,
        to: undefined,
    };

    @Input()
    predefiniteIntervals: SeFromToInterval[] = [
        new SeFromToInterval(0, 0, 0, 0, 'today'),
        new SeFromToInterval(0, 0, 0, 0, 'yesterday'),
        new SeFromToInterval(0, 1, 0, 0),
        new SeFromToInterval(0, 2, 0, 0),
        new SeFromToInterval(0, 0, 1, 0),
        new SeFromToInterval(0, 0, 6, 0),
        new SeFromToInterval(0, 0, 0, 1),
    ];

    @Output()
    fromToDatesChange: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    searchRequired: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    predefinedIntervalSet: EventEmitter<{ propertyName: string; interval: SeFromToInterval; }> = new EventEmitter<{
        propertyName: string;
        interval: SeFromToInterval;
    }>();

    // Two Way
    @Output() isOnErrorChange = new EventEmitter();
    _isOnError: boolean = false;

    @Input()
    get isOnError() {
        return this._isOnError;
    }

    set isOnError(value) {
        if (this._isOnError != value) {
            this._isOnError = value;
        }
    }

    // Constructor
    constructor(private translateService: TranslateService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.fromToModel) {
            this.setupUtcBeginEnd();
            this.validateDates();
        }
    }

    // private functions
    private setupUtcBeginEnd(): void {
        // imposto la durata fino alla fine della giornata
        if (this.fromToModel && this.fromToModel.to) {
            if (this.fromToModel.to instanceof Date) {
                // Date
                this.fromToModel.to.setHours(23, 59, 59, 0);
            } else if (moment.isMoment(this.fromToModel.to)) {
                // Moment
                this.fromToModel.to = this.fromToModel.to.utc(true); // Aggiunge il fuso, poi lo toglierà automaticamente il toJson
                this.fromToModel.to.set({ hour: 23, minute: 59, second: 59, millisecond: 0 });
            }

            this.toPickerMatcher.to = this.fromToModel.to;
            this.fromPickerMatcher.to = this.fromToModel.to;
        }

        if (this.fromToModel && this.fromToModel.from) {
            if (this.fromToModel.from instanceof Date) {
                // Date
                this.fromToModel.from.setHours(0, 0, 0, 0);
            } else if (moment.isMoment(this.fromToModel.from)) {
                // Moment
                this.fromToModel.from = this.fromToModel.from.utc(true);
                this.fromToModel.from.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            }

            this.toPickerMatcher.from = this.fromToModel.from;
            this.fromPickerMatcher.from = this.fromToModel.from;
        }
    }

    // public functions

    ngOnDestroy() {
        if (this.isOnErrorFromSubscriber) this.isOnErrorFromSubscriber.unsubscribe();

        if (this.isOnErrorToSubscriber) this.isOnErrorToSubscriber.unsubscribe();
    }

    ngOnInit(): void {
        this.setupUtcBeginEnd();
        this.validateDates();

        this.isOnErrorFromSubscriber = this.fromPickerMatcher.isOnError.subscribe((value) => {
            this.isOnErrorHandler(value);
        });

        this.isOnErrorToSubscriber = this.toPickerMatcher.isOnError.subscribe((value) => {
            this.isOnErrorHandler(value);
        });
    }

    private isOnErrorHandler(value: boolean) {
        if (this.isOnError != value) {
            this.isOnError = value;
            setTimeout(() => {
                // Il timeout serve per la sincronizzazione altrimenti abbiamo l'errore i debug
                this.isOnErrorChange.emit(value);
            }, 0);
        }
    }

    getLabelForInterval(interval: SeFromToInterval): string {
        if (interval.label === 'today' || interval.label === 'yesterday') {
            return this.translateService.instant(interval.label);
        }

        if (interval.days) {
            const translateLabel = interval.days > 1 ? 'Days' : 'Day';
            return interval.days.toString() + ' ' + this.translateService.instant(translateLabel);
        }

        if (interval.weeks) {
            const translateLabel = interval.weeks > 1 ? 'Weeks' : 'Week';
            return interval.weeks.toString() + ' ' + this.translateService.instant(translateLabel);
        }

        if (interval.months) {
            const translateLabel = interval.months > 1 ? 'Months' : 'Month';
            return interval.months.toString() + ' ' + this.translateService.instant(translateLabel);
        }

        if (interval.years) {
            return interval.years.toString() + ' ' + this.translateService.instant('Year');
        }

        return '';
    }

    onFromToChange(_: any) {
        this.setupUtcBeginEnd();
        this.validateDates();
        this.fromToDatesChange.emit({ propertyName: this.id });
    }

    onDateInput(event: MatDatepickerInputEvent<any>, field: 'from' | 'to') {
        if (field === 'from') {
            this.fromToModel.from = event.value;
        } else {
            this.fromToModel.to = event.value;
        }

        this.setupUtcBeginEnd();
        this.validateDates();
        this.fromToDatesChange.emit({ propertyName: this.id });
    }

    resetDateFrom(e: Event) {
        e.preventDefault(); // Altrimenti parte la ricerca sulla search page
        this.fromToModel.from = undefined;
        this.setupUtcBeginEnd();
        this.validateDates();
        this.fromToDatesChange.emit({ propertyName: this.id });
    }

    resetDateTo(e: any) {
        e.preventDefault(); // Altrimenti parte la ricerca sulla search page
        this.fromToModel.to = undefined;
        this.setupUtcBeginEnd();
        this.validateDates();
        this.fromToDatesChange.emit({ propertyName: this.id });
    }

    onSpaceBar(_: KeyboardEvent) {
        // Imposto dalle 00.00 alle 23.59 di oggi
        let from: moment.Moment = moment(Date.now());

        from.hour(0);
        from.minute(0);
        from.second(0);

        this.fromToModel.from = from.utc(true);

        let to: moment.Moment = moment(Date.now());
        to.hour(23);
        to.minute(59);
        to.second(59);

        this.fromToModel.to = to.utc(true);

        this.setupUtcBeginEnd();
        this.validateDates();
        this.fromToDatesChange.emit({ propertyName: this.id });
    }

    openPredefiniteInterval(e: any) {
        e.preventDefault(); // Altrimenti parte la ricerca sulla search page
    }

    setPredefiniteInterval(interval: SeFromToInterval) {
        if (interval.label === 'today') {
            this.fromToModel.from = moment().utc().startOf('day');
            this.fromToModel.to = moment().utc().endOf('day');
        } else if (interval.label === 'yesterday') {
            this.fromToModel.from = moment().subtract(1, 'days').utc().startOf('day');
            this.fromToModel.to = moment().subtract(1, 'days').utc().endOf('day');
        } else {
            this.fromToModel.from = new Date(
                new Date().getFullYear() - interval.getYears(),
                new Date().getMonth() - interval.getMonths(),
                new Date().getDate() - interval.getDays(),
            );
            this.fromToModel.to = new Date(
                new Date().getFullYear(),
                new Date().getMonth(),
                new Date().getDate(),
                23,
                59,
                59,
            );
        }

        this.setupUtcBeginEnd();
        this.validateDates();
        this.predefinedIntervalSet.emit({ propertyName: this.id, interval: interval });
    }

    onSearchRequired(e: any) {
        this.setupUtcBeginEnd();
        this.validateDates();

        e.preventDefault();
        this.searchRequired.emit();
    }

    getDateFormat() {
        return SE_UI_DATE_FORMATS.display.dateInput;
    }

    validateDates() {
        const from = this.fromToModel.from ? moment(this.fromToModel.from) : null;
        const to = this.fromToModel.to ? moment(this.fromToModel.to) : null;

        const now = moment().startOf('day');

        const isInvalid =
            (!!from && from.isAfter(now, 'day')) ||
            (!!to && to.isBefore(now, 'day')) ||
            (!!from && !!to && from.isAfter(to));

        this.fromPickerMatcher.fromAfterTo = isInvalid;
        this.toPickerMatcher.fromAfterTo = isInvalid;

        this.fromPickerMatcher.from = from?.toDate();
        this.fromPickerMatcher.to = to?.toDate();

        this.toPickerMatcher.from = from?.toDate();
        this.toPickerMatcher.to = to?.toDate();

        this.fromPickerMatcher.isOnError.next(isInvalid);
        this.toPickerMatcher.isOnError.next(isInvalid);
    }
}

export class SeFromToInterval {
    constructor(
        public days: number,
        public weeks: number,
        public months: number,
        public years: number,
        public label?: string,
    ) {}

    getDays(): number {
        if (!this.weeks) return this.days;
        else return this.weeks * 7 + this.days;
    }

    getMonths(): number {
        return this.months;
    }

    getYears(): number {
        return this.years;
    }
    getLabel(): string {
        return this.label ?? '';
    }
}

export class FromToPickerStateMatcher implements ErrorStateMatcher {
    isOnError: BehaviorSubject<boolean> = new BehaviorSubject(false);
    to: any;
    from: any;
    fromAfterTo: boolean = false;
    toBeforeFrom: boolean = false;

    isErrorState(control: FormControl | null, _: FormGroupDirective | NgForm | null): boolean {
        let _isOnError = !!(control && control.invalid);
        if (typeof this.from === 'string') {
            this.from = new Date(this.from).getTime();
        }
        if (typeof this.to === 'string') {
            this.to = new Date(this.to).getTime();
        }

        this.fromAfterTo = this.from > this.to;
        _isOnError = _isOnError || this.fromAfterTo == true;
        if (_isOnError != this.isOnError.value) this.isOnError.next(_isOnError);

        return _isOnError;
    }
}
